import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { SmsTemplate } from "@/types/sms-queue";
import { getAvailableVariables, previewSmsMessage } from "@/utils/smsUtils";

const templateSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  message: z.string().min(1, "Mensagem é obrigatória").max(160, "Mensagem não pode exceder 160 caracteres"),
  is_default: z.boolean().default(false),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface SmsTemplateFormProps {
  template?: SmsTemplate | null;
  onSubmit: (data: Omit<SmsTemplate, "id" | "created_at" | "user_id">) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SmsTemplateForm = ({ template, onSubmit, onCancel, isLoading }: SmsTemplateFormProps) => {
  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name || "",
      message: template?.message || "",
      is_default: template?.is_default || false,
    },
  });

  const message = form.watch("message");
  const availableVariables = getAvailableVariables();

  const handleSubmit = (data: TemplateFormData) => {
    onSubmit({
      name: data.name,
      message: data.message,
      is_default: data.is_default,
      variables: availableVariables,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome do Template</FormLabel>
              <FormControl>
                <Input placeholder="Ex: Cupom de Boas-vindas" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mensagem SMS</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Digite sua mensagem..."
                  className="min-h-[100px]"
                  maxLength={160}
                  {...field}
                />
              </FormControl>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>{(field.value || "").length}/160 caracteres</span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <label className="text-sm font-medium">Variáveis disponíveis:</label>
          <div className="flex flex-wrap gap-1">
            {availableVariables.map((variable) => (
              <Badge key={variable} variant="outline" className="text-xs cursor-pointer"
                onClick={() => {
                  const currentMessage = form.getValues("message");
                  form.setValue("message", currentMessage + `{${variable}}`);
                }}
              >
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>

        {message && (
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm font-medium mb-2">Preview:</p>
            <p className="text-sm">{previewSmsMessage(message)}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {template ? "Atualizar" : "Criar"} Template
          </Button>
        </div>
      </form>
    </Form>
  );
};